import { InfoCircleOutlined, WarningTwoTone } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { Button, Checkbox, Col, Tooltip, theme } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type React from 'react';
import { memo, useCallback, useMemo } from 'react';
import type { ParameterSelection_RequestableParameterFragment, ParameterToAddInput } from '../../../../graphql/generated/graphql.ts';
import type { SheetData, SheetGroup, SheetParameter } from '../../../../graphql/sheet';

const SheetParameterCol: React.FC<{
  sheet: SheetData;
  group: SheetGroup;
  sheetParam: SheetParameter;
  requestableParam?: ParameterSelection_RequestableParameterFragment;
  available: boolean;
  compatible: boolean;
  selected: boolean;
  onAdd: (inputs: ParameterToAddInput[]) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
  reorder: boolean;
  flipParamNames: boolean;
  highlight: string;
}> = ({
  sheet,
  group,
  sheetParam,
  requestableParam,
  available,
  compatible,
  selected,
  onAdd,
  onRemove,
  onInfo,
  reorder,
  flipParamNames,
  highlight,
}) => {
  const { token } = theme.useToken();

  // Sheets highlight instead of filtering — the layout mirrors the paper form, so entries must
  // keep their position. Only wrap while searching: a full sheet is hundreds of parameters and
  // every Markable spins up its own mark.js instance.
  const mark = (text: string) => (highlight ? <Markable tokens={highlight}>{text}</Markable> : text);

  const onChange = useCallback(
    (e: CheckboxChangeEvent) => {
      e.target.checked
        ? onAdd([
            {
              parameterId: requestableParam!.id,
              preferredBillingType: null,
              preferredDiagnoseIds: [],
            },
          ])
        : onRemove(requestableParam!.id);
    },
    [onAdd, onRemove, requestableParam]
  );

  const id = useMemo(() => `${sheet.name}_${group.name}_${sheetParam.id}`, [group, sheet, sheetParam]);

  const styles = useMemo(
    () => ({
      col: css`
        display: flex;
        gap: 8px;
        align-items: center;
        height: 24px;
      `,
      label: css`
        display: flex;
        min-width: 0;
        cursor: ${available && compatible ? 'pointer' : 'not-allowed'};
        opacity: ${available && compatible ? 1 : 0.5};
      `,
      text1: css`
        flex-shrink: 0;
        margin-right: ${token.marginXXS}px;
        display: inline-block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
      `,
      text2: css`
        margin-right: ${token.marginXXS}px;
        display: inline-block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: ${token.colorTextDescription};
      `,
      // Kurzname is the identifier — always monospace, wherever flipParamNames places it.
      mono: css`
        font-family: ${token.fontFamilyCode};
      `,
      div: css`
        flex-grow: 1;
        justify-content: end;
        display: flex;
      `,
      warning: css`
        margin-right: 5px;
      `,
    }),
    [available, compatible, token]
  );

  return (
    <Col lg={12} xl={8} className={styles.col}>
      <Checkbox id={id} checked={selected} disabled={!available || !compatible} onChange={onChange} />
      <label htmlFor={id} className={styles.label}>
        <span className={cx(styles.text1, !flipParamNames && styles.mono)}>
          {mark(flipParamNames ? sheetParam.longName : sheetParam.shortName)}
        </span>
        <span className={cx(styles.text2, flipParamNames && styles.mono)}>
          <small>{mark(flipParamNames ? sheetParam.shortName : sheetParam.longName)}</small>
        </span>
      </label>
      <div className={styles.div}>
        {available && compatible && (
          <Button
            icon={<InfoCircleOutlined twoToneColor={token.colorInfo} />}
            size="small"
            type="text"
            onClick={() => onInfo(requestableParam!)}
          />
        )}
        {(!available || !compatible) && (
          <Tooltip
            title={
              available && !compatible
                ? 'Der Parameter kann nicht angefordert werden, da er mit den ausgewählten Parametern nicht kompatibel ist.'
                : `Der Parameter kann aktuell nicht angefordert werden. ${
                    reorder ? 'Nachforderung nicht möglich.' : 'Bitte kontaktieren Sie das Labor für weitere Informationen.'
                  }`
            }
          >
            <WarningTwoTone twoToneColor={token.colorWarning} className={styles.warning} />
          </Tooltip>
        )}
      </div>
    </Col>
  );
};

export const MemoSheetParameterCol = memo(SheetParameterCol);
